import gsap from 'gsap';
import appStore from '../utils/state';
import { getSectionHTML } from '../utils/section-renderer';
import isTailwindBreakpoint from '../utils/isTailwindBreakpoint';
import { getCssVarInPx } from '../utils/getCssVarInPx';

type SearchRedirect = {
  label: string;
  keywords: string[];
  url: string;
};

type PredictiveQuery = {
  text?: string;
  url?: string;
};

type PredictiveCollection = {
  title?: string;
  url?: string;
};

type PredictiveProduct = {
  handle?: string;
  url?: string;
};

type PredictiveResults = {
  queries: PredictiveQuery[];
  collections: PredictiveCollection[];
  products: PredictiveProduct[];
};

export default class SearchModal extends HTMLElement {
  private searchModalOpenUnsubscribe: (() => void) | null = null;
  private globalKeyDownBound = this.onGlobalKeyDown.bind(this);

  private showTl: gsap.core.Timeline | null = null;
  private hideTl: gsap.core.Timeline | null = null;

  private searchInput: HTMLInputElement | null = null;
  private recommendations: HTMLElement | null = null;
  private results: HTMLElement | null = null;
  private queries: HTMLUListElement | null = null;
  private queriesContainer: HTMLElement | null = null;
  private queryItemTemplate: HTMLTemplateElement | null = null;
  private products: HTMLUListElement | null = null;
  private productsEmpty: HTMLElement | null = null;
  private productItemTemplate: HTMLTemplateElement | null = null;
  private productsSkeleton: DocumentFragment | null = null;
  private collections: HTMLUListElement | null = null;
  private collectionsEmpty: HTMLElement | null = null;
  private collectionItemTemplate: HTMLTemplateElement | null = null;

  private redirects: SearchRedirect[] = [];
  private defaultSubmitLabel = 'Search';
  private redirectSubmitLabelTemplate = 'Go to [label]';
  private submitButtonLabel: HTMLSpanElement | null = null;
  private searchForm: HTMLFormElement | null = null;

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private predictiveRequestId = 0;

  private unsubscribeFromIsMainNavOpen: (() => void) | null = null;
  private unsubscribeFromIsCartModalOpen: (() => void) | null = null;
  private unsubscribeFromSearchValue: (() => void) | null = null;

  private onInputBound = this.onInput.bind(this);
  private onSearchValueChangeBound = this.onSearchValueChange.bind(this);
  private onFormSubmitBound = this.onFormSubmit.bind(this);
  private onMainNavUpdateBound = this.onMainNavUpdate.bind(this);
  private onCartModalUpdateBound = this.onCartModalUpdate.bind(this);
  private onVisualViewportUpdateBound = this.onVisualViewportUpdate.bind(this);
  private onWindowResizeBound = this.onWindowResize.bind(this);

  connectedCallback(): void {
    this.parseRedirectsData();
    this.defaultSubmitLabel = this.dataset.defaultSubmitLabel || this.defaultSubmitLabel;
    this.redirectSubmitLabelTemplate = this.dataset.redirectSubmitLabelTemplate || this.redirectSubmitLabelTemplate;
    this.submitButtonLabel = this.querySelector<HTMLSpanElement>(
      '[data-search-modal-ref="submit-button"] [data-button-modular-ref="labels"] > span:first-child',
    );

    this.searchInput = this.querySelector<HTMLInputElement>('[data-ref-search-modal="search-input"]') ?? null;

    this.recommendations = this.querySelector('[data-ref-search-modal="recommendations"]');
    this.results = this.querySelector('[data-ref-search-modal="results"]');
    this.queriesContainer = this.querySelector('[data-ref-search-modal="queries-container"]');
    this.queries = this.querySelector('[data-ref-search-modal="queries"]');
    this.queryItemTemplate = this.querySelector('[data-ref-search-modal="query-item-template"]');
    this.collectionItemTemplate = this.querySelector('[data-ref-search-modal="collection-item-template"]');

    this.products = this.querySelector('[data-ref-search-modal="products"]');
    this.productsEmpty = this.querySelector('[data-ref-search-modal="products-empty"]');
    this.productItemTemplate = this.querySelector('[data-ref-search-modal="product-item-template"]');
    this.collections = this.querySelector('[data-ref-search-modal="collections"]');
    this.collectionsEmpty = this.querySelector('[data-ref-search-modal="collections-empty"]');

    const mobileForm = this.querySelector<HTMLFormElement>('[data-ref-search-modal="form"]');
    this.searchForm = mobileForm;

    this.cacheProductsSkeleton();

    this.addEventListeners();
    this.subscribe();
    this.syncMobileViewportSize();

    this.applySearchMode(false);

    this.searchModalOpenUnsubscribe = appStore.subscribeKey('isSearchModalOpen', (isOpen) => {
      if (isOpen) {
        this.show();
      } else {
        this.hide();
      }
    });
  }

  addEventListeners(): void {
    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.onInputBound);
    }

    this.searchForm?.addEventListener('submit', this.onFormSubmitBound);

    window.addEventListener('keydown', this.globalKeyDownBound);
    window.addEventListener('resize', this.onWindowResizeBound);
    window.visualViewport?.addEventListener('resize', this.onVisualViewportUpdateBound);
    window.visualViewport?.addEventListener('scroll', this.onVisualViewportUpdateBound);
  }

  removeEventListeners(): void {
    if (this.searchInput) {
      this.searchInput.removeEventListener('input', this.onInputBound);
    }

    this.searchForm?.removeEventListener('submit', this.onFormSubmitBound);

    window.removeEventListener('keydown', this.globalKeyDownBound);
    window.removeEventListener('resize', this.onWindowResizeBound);
    window.visualViewport?.removeEventListener('resize', this.onVisualViewportUpdateBound);
    window.visualViewport?.removeEventListener('scroll', this.onVisualViewportUpdateBound);
  }

  disconnectedCallback(): void {
    this.searchModalOpenUnsubscribe?.();
    this.searchModalOpenUnsubscribe = null;

    this.removeEventListeners();

    if (this.showTl) {
      this.showTl.kill();
      this.showTl = null;
    }
    if (this.hideTl) {
      this.hideTl.kill();
      this.hideTl = null;
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    this.unsubscribe();
  }

  subscribe(): void {
    this.unsubscribeFromIsMainNavOpen = appStore.subscribeKey('isMainNavOpen', this.onMainNavUpdateBound);
    this.unsubscribeFromIsCartModalOpen = appStore.subscribeKey('isCartModalOpen', this.onCartModalUpdateBound);
    this.unsubscribeFromSearchValue = appStore.subscribeKey('searchValue', this.onSearchValueChangeBound);
  }

  onMainNavUpdate(): void {
    if (appStore.getState().isMainNavOpen) {
      appStore.actions.closeSearchModal();
    }
  }

  onCartModalUpdate(): void {
    if (appStore.getState().isCartModalOpen) {
      this.hide();
    }
  }

  unsubscribe(): void {
    if (this.unsubscribeFromSearchValue) {
      this.unsubscribeFromSearchValue();
      this.unsubscribeFromSearchValue = null;
    }

    if (this.unsubscribeFromIsMainNavOpen) {
      this.unsubscribeFromIsMainNavOpen();
      this.unsubscribeFromIsMainNavOpen = null;
    }

    if (this.unsubscribeFromIsCartModalOpen) {
      this.unsubscribeFromIsCartModalOpen();
      this.unsubscribeFromIsCartModalOpen = null;
    }
  }

  private onSearchValueChange(searchValue: string): void {
    const query = searchValue.trim();

    if (!query) {
      appStore.actions.setSearchRedirect(null);
      this.applySearchMode(false);
      this.updateSubmitButtonLabel(null);
      return;
    }

    const redirect = this.findMatchingRedirect(query);
    appStore.actions.setSearchRedirect(redirect?.url ?? null);
    this.updateSubmitButtonLabel(redirect);
    this.applySearchMode(true);
    this.resetResultsToLoadingState();

    this.predictiveRequestId += 1;
    const requestId = this.predictiveRequestId;

    void this.handlePredictiveQuery(query, requestId);
  }

  private parseRedirectsData(): void {
    const jsonEl = this.querySelector<HTMLScriptElement>('[data-ref-search-modal="redirects-data"]');
    if (!jsonEl?.textContent) {
      return;
    }

    try {
      const parsed: unknown = JSON.parse(jsonEl.textContent);
      if (!Array.isArray(parsed)) {
        return;
      }

      this.redirects = parsed
        .filter((item): item is SearchRedirect => this.isSearchRedirect(item))
        .filter((redirect) => redirect.url.trim() !== '' && redirect.keywords.length > 0);
    } catch (error) {
      console.error(error);
    }
  }

  private isSearchRedirect(value: unknown): value is SearchRedirect {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const redirect = value as Partial<SearchRedirect>;
    return (
      typeof redirect.label === 'string' &&
      Array.isArray(redirect.keywords) &&
      redirect.keywords.every((keyword) => typeof keyword === 'string') &&
      typeof redirect.url === 'string'
    );
  }

  private findMatchingRedirect(query: string): SearchRedirect | null {
    const normalized = query.trim().toLocaleLowerCase();
    if (!normalized) {
      return null;
    }

    for (const redirect of this.redirects) {
      for (const keyword of redirect.keywords) {
        if (keyword.trim().toLocaleLowerCase() === normalized) {
          return redirect;
        }
      }
    }

    return null;
  }

  private updateSubmitButtonLabel(redirect: SearchRedirect | null): void {
    if (!this.submitButtonLabel) {
      return;
    }

    if (redirect) {
      this.submitButtonLabel.textContent = this.redirectSubmitLabelTemplate.replace('[label]', redirect.label);
      return;
    }

    this.submitButtonLabel.textContent = this.defaultSubmitLabel;
  }

  private onFormSubmit(event: SubmitEvent): void {
    const { isSearchRedirect, searchRedirectUrl } = appStore.getState();
    if (!isSearchRedirect || !searchRedirectUrl) {
      return;
    }

    event.preventDefault();
    window.location.assign(searchRedirectUrl);
  }

  private onGlobalKeyDown(event: KeyboardEvent): void {
    if (!appStore.getState().isSearchModalOpen) {
      return;
    }

    if (event.key === 'Escape') {
      appStore.actions.closeSearchModal();
    }
  }

  private onVisualViewportUpdate(): void {
    this.syncMobileViewportSize();
  }

  private onWindowResize(): void {
    this.syncMobileViewportSize();
  }

  private syncMobileViewportSize(): void {
    if (isTailwindBreakpoint('lg')) {
      this.style.removeProperty('top');
      this.style.removeProperty('height');
      this.style.removeProperty('bottom');
      return;
    }

    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }

    const headerHeight = getCssVarInPx('--header-height');
    this.style.top = `${viewport.offsetTop + headerHeight}px`;
    this.style.height = `${Math.max(0, viewport.height - headerHeight)}px`;
    this.style.bottom = 'auto';
  }

  private show(): void {
    if (this.hideTl) {
      this.hideTl.kill();
    }
    if (this.showTl) {
      this.showTl.kill();
    }

    this.syncMobileViewportSize();

    this.showTl = gsap.timeline();
    this.showTl.fromTo(this, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0, ease: 'power2.out' }, 0);
    this.showTl.add(() => {
      this.syncMobileViewportSize();

      if (this.searchInput) {
        this.searchInput.focus();
      }
    }, 0.2);
  }

  private hide(): void {
    if (this.showTl) {
      this.showTl.kill();
    }
    if (this.hideTl) {
      this.hideTl.kill();
    }

    this.hideTl = gsap.timeline();
    this.hideTl.to(this, { autoAlpha: 0, duration: 0, ease: 'power2.out' }, 0);
  }

  private onInput(): void {
    if (!this.searchInput) {
      return;
    }

    appStore.actions.updateSearchValue(this.searchInput.value);
  }

  private buildPredictiveUrl(query: string): URL {
    // @ts-expect-error Shopify is injected on the storefront
    const url = new URL(`${window.Shopify.routes.root}search/suggest.json`, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'query,product,collection');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set(
      'resources[fields]',
      'product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor',
    );
    return url;
  }

  private applySearchMode(hasQuery: boolean): void {
    if (this.recommendations) {
      this.recommendations.classList.toggle('block', !hasQuery);
      this.recommendations.classList.toggle('hidden', hasQuery);
    }

    if (this.results) {
      this.results.classList.toggle('hidden', !hasQuery);
      this.results.classList.toggle('flex', hasQuery);
    }
  }

  private cacheProductsSkeleton(): void {
    if (!this.products) {
      return;
    }

    this.productsSkeleton = document.createDocumentFragment();
    const skeletonLis = this.products.querySelectorAll(':scope > li');

    skeletonLis.forEach((li) => {
      this.productsSkeleton!.appendChild(li.cloneNode(true));
    });
  }

  private resetResultsToLoadingState(): void {
    if (this.queriesContainer) {
      this.queriesContainer.classList.remove('block');
      this.queriesContainer.classList.add('hidden');
    }

    if (this.queries) {
      this.queries.replaceChildren();
    }

    if (this.collections) {
      this.collections.replaceChildren();
    }

    if (this.collectionsEmpty) {
      this.collectionsEmpty.classList.add('hidden');
    }

    this.resetProductsToLoadingState();
  }

  private resetProductsToLoadingState(): void {
    if (!this.products || !this.productItemTemplate || !this.productsSkeleton) {
      return;
    }

    if (this.productsEmpty) {
      this.productsEmpty.classList.add('hidden');
    }

    const skeletonNodes = Array.from(this.productsSkeleton.childNodes).map((node) => node.cloneNode(true));
    this.products.replaceChildren(...skeletonNodes, this.productItemTemplate);
  }

  private async handlePredictiveQuery(query: string, requestId: number): Promise<void> {
    const trimmed = query.trim();

    if (!trimmed) {
      return;
    }

    try {
      const response = await fetch(this.buildPredictiveUrl(trimmed), { method: 'GET' });

      if (requestId !== this.predictiveRequestId) {
        return;
      }

      if (!response.ok) {
        throw new Error(`Predictive search failed (${response.status})`);
      }

      const json: unknown = await response.json();
      const parsed = this.parsePredictiveResponse(json);

      this.renderPredictiveResults(parsed, requestId);
    } catch (error) {
      console.error(error);
    }
  }

  private parsePredictiveResponse(json: unknown): PredictiveResults {
    const fallback: PredictiveResults = { queries: [], collections: [], products: [] };

    if (!json || typeof json !== 'object') {
      return fallback;
    }

    const response = json as {
      resources?: {
        results?: {
          queries?: PredictiveQuery[];
          collections?: PredictiveCollection[];
          products?: PredictiveProduct[];
        };
      };
    };

    const results = response.resources?.results;
    if (!results) {
      return fallback;
    }

    return {
      queries: Array.isArray(results.queries) ? results.queries : [],
      collections: Array.isArray(results.collections) ? results.collections : [],
      products: Array.isArray(results.products) ? results.products : [],
    };
  }

  private renderPredictiveResults(results: PredictiveResults, requestId: number): void {
    if (requestId !== this.predictiveRequestId) {
      return;
    }

    const queries = results.queries.slice(0, 5);
    const products = results.products.slice(0, 6);
    const collections = results.collections.slice(0, 5);

    this.renderQueries(queries);
    this.renderCollections(collections);
    void this.renderProducts(products, requestId);
  }

  private renderQueries(queries: PredictiveQuery[]): void {
    if (!this.queriesContainer) {
      return;
    }

    if (queries.length === 0) {
      this.queriesContainer.classList.remove('block');
      this.queriesContainer.classList.add('hidden');
      return;
    }

    this.queriesContainer.classList.remove('hidden');
    this.queriesContainer.classList.add('block');

    if (!this.queries || !this.queryItemTemplate) {
      return;
    }

    this.queries.replaceChildren(
      ...queries.map((query) => {
        const fragment = this.queryItemTemplate!.content.cloneNode(true) as DocumentFragment;
        const li = fragment.querySelector('li') as HTMLLIElement | null;
        const a = fragment.querySelector('a') as HTMLAnchorElement | null;

        const text = (query.text ?? '').trim();
        const url = (query.url ?? '').trim();
        const currentInput = appStore.getState().searchValue.trim();

        if (a && url) {
          a.href = url;
          this.renderQuerySuggestionContent(a, this.splitQuerySuggestionParts(text, currentInput));
        }

        return li ?? document.createElement('li');
      }),
    );
  }

  private splitQuerySuggestionParts(
    suggestion: string,
    input: string,
  ): { prefix: string; match: string; suffix: string } {
    if (!suggestion) {
      return { prefix: '', match: '', suffix: '' };
    }

    if (!input) {
      return { prefix: '', match: '', suffix: suggestion };
    }

    const matchIndex = suggestion.toLocaleLowerCase().indexOf(input.toLocaleLowerCase());
    if (matchIndex === -1) {
      return { prefix: '', match: '', suffix: suggestion };
    }

    return {
      prefix: suggestion.slice(0, matchIndex),
      match: suggestion.slice(matchIndex, matchIndex + input.length),
      suffix: suggestion.slice(matchIndex + input.length),
    };
  }

  private renderQuerySuggestionContent(
    anchor: HTMLAnchorElement,
    parts: { prefix: string; match: string; suffix: string },
  ): void {
    anchor.replaceChildren();

    if (parts.prefix) {
      anchor.appendChild(this.createQuerySuggestedSpan(parts.prefix));
    }

    if (parts.match) {
      anchor.appendChild(this.createQueryInputSpan(parts.match));
    }

    if (parts.suffix) {
      anchor.appendChild(this.createQuerySuggestedSpan(parts.suffix));
    }
  }

  private createQuerySuggestedSpan(text: string): HTMLSpanElement {
    const span = document.createElement('span');
    span.dataset.querySuggested = '';
    span.className = 'fs-body-l-capitalized';
    span.textContent = text;
    return span;
  }

  private createQueryInputSpan(text: string): HTMLSpanElement {
    const span = document.createElement('span');
    span.dataset.queryInput = '';
    span.className = 'fs-body-l-medium';
    span.textContent = text;
    return span;
  }

  private renderCollections(collections: PredictiveCollection[]): void {
    if (!this.collections || !this.collectionItemTemplate) {
      return;
    }

    if (collections.length === 0) {
      this.collections.replaceChildren();

      if (this.collectionsEmpty) {
        this.collectionsEmpty.classList.remove('hidden');
      }

      return;
    }

    if (this.collectionsEmpty) {
      this.collectionsEmpty.classList.add('hidden');
    }

    this.collections.replaceChildren(
      ...collections.map((collection) => {
        const fragment = this.collectionItemTemplate!.content.cloneNode(true) as DocumentFragment;
        const li = fragment.querySelector('li') as HTMLLIElement | null;
        const a = fragment.querySelector('a') as HTMLAnchorElement | null;

        const title = (collection.title ?? '').trim();
        const url = (collection.url ?? '').trim();

        if (a && title && url) {
          const bracketMatch = title.match(/^\[([^\]]+)\]\s*(.*)$/);
          let formatted = title;
          if (bracketMatch) {
            const bracketPart = bracketMatch[1].trim();
            const rest = bracketMatch[2].trim();
            formatted = rest ? `${bracketPart} – ${rest}` : bracketPart;
          }

          a.textContent = formatted
            .replace(/Grouper Tag/gi, '')
            .replace(/\b(CAT|SUB)\b/gi, '')
            .replace(/\s+/g, ' ')
            .replace(/\s*–\s*/g, ' – ')
            .replace(/^–\s*|\s*–$/g, '')
            .trim()
            .toLowerCase();
          a.href = url;
        }

        return li ?? document.createElement('li');
      }),
    );
  }

  private async renderProducts(products: PredictiveProduct[], requestId: number): Promise<void> {
    if (!this.products || !this.productItemTemplate) {
      return;
    }

    const cards: Element[] = [];

    for (const product of products) {
      if (requestId !== this.predictiveRequestId) {
        return;
      }

      if (!product.handle) {
        continue;
      }

      const cardHtml = await this.getProductCardHtml(product.handle);

      if (requestId !== this.predictiveRequestId) {
        return;
      }

      if (cardHtml) {
        cards.push(cardHtml);
      }
    }

    if (requestId !== this.predictiveRequestId) {
      return;
    }

    if (cards.length > 0) {
      if (this.productsEmpty) {
        this.productsEmpty.classList.add('hidden');
      }

      this.products.replaceChildren(...cards, this.productItemTemplate);
      return;
    }

    this.products.replaceChildren(this.productItemTemplate);

    if (this.productsEmpty) {
      this.productsEmpty.classList.remove('hidden');
    }
  }

  private async getProductCardHtml(handle: string): Promise<Element | null> {
    try {
      const sectionHtml = await getSectionHTML(
        'mobile-product-card-ajax',
        new URL(`/products/${handle}`, window.location.origin),
        false,
      );

      const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const card = parsed.querySelector('product-card');

      if (!card) {
        return null;
      }

      return card;
    } catch (error) {
      console.error(error);
      return null;
    }
  }
}

customElements.define('search-modal', SearchModal);
